import { shouldRefresh, TOKEN_EXPIRED_CODE } from 'shared';

/**
 * The API client, and the one place token lifecycle is handled.
 *
 * Two rules from the SPEC live here:
 *   1. Refresh pre-emptively when the access token is within 5 minutes of
 *      expiring, so a request never leaves with a token that dies in flight.
 *   2. If a request comes back 401 with `token_expired` anyway — a clock skew,
 *      a long-suspended tab — refresh once against the 50-day token and replay.
 *
 * Concurrent callers share a single in-flight refresh. Without that, six
 * requests firing on page load would spend six refresh tokens and, because
 * refresh tokens rotate, five of them would look like a replay attack.
 */

const BASE = '/api';

const STORAGE_KEY = '3ojo.session';

export interface Session {
  accessToken: string;
  refreshToken: string;
}

let session: Session | null = null;
let refreshInFlight: Promise<Session | null> | null = null;
const listeners = new Set<(session: Session | null) => void>();

function readStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

session = readStoredSession();

export function getSession(): Session | null {
  return session;
}

export function setSession(next: Session | null): void {
  session = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private browsing can refuse storage. The session still works for this
    // tab; it just will not survive a reload.
  }
  for (const listener of listeners) listener(next);
}

export function onSessionChange(listener: (session: Session | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The caller needs to sign in. */
  get isAuth(): boolean {
    return this.status === 401;
  }

  /** The caller needs a paid tier. */
  get isUpgrade(): boolean {
    return this.status === 402;
  }

  get isOffline(): boolean {
    return this.status === 0;
  }
}

async function performRefresh(): Promise<Session | null> {
  const current = session;
  if (!current?.refreshToken) return null;

  try {
    const response = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    });

    if (!response.ok) {
      // The refresh token is spent, revoked or expired. There is no recovering
      // from here, so drop the session and let the UI ask for a sign-in.
      setSession(null);
      return null;
    }

    const data = (await response.json()) as Session;
    const next = { accessToken: data.accessToken, refreshToken: data.refreshToken };
    setSession(next);
    return next;
  } catch {
    // A network failure is not proof the token is bad — keep it and retry later.
    return null;
  }
}

function refresh(): Promise<Session | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Send without a token even if one is held. */
  anonymous?: boolean;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers = new Headers(options.headers);
  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  return fetch(buildUrl(path, options.query), {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
}

export async function apiFetch(path: string, options: RequestOptions = {}): Promise<Response> {
  let token = options.anonymous ? null : session?.accessToken ?? null;

  // Rule 1: swap a nearly-dead token before spending a request on it.
  if (!options.anonymous && token && shouldRefresh(token)) {
    token = (await refresh())?.accessToken ?? null;
  }

  let response: Response;
  try {
    response = await send(path, options, token);
  } catch {
    throw new ApiError(0, 'You appear to be offline.', 'offline');
  }

  // Rule 2: one retry after an unexpected expiry.
  if (response.status === 401 && !options.anonymous && session) {
    const body = await response.clone().json().catch(() => null);
    if ((body as { code?: string } | null)?.code === TOKEN_EXPIRED_CODE) {
      const next = await refresh();
      if (next) {
        try {
          response = await send(path, options, next.accessToken);
        } catch {
          throw new ApiError(0, 'You appear to be offline.', 'offline');
        }
      }
    }
  }

  return response;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await apiFetch(path, options);

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: string; code?: string }
      | null;
    throw new ApiError(
      response.status,
      body?.error ?? `Request failed with ${response.status}`,
      body?.code,
      body,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
